import { useMemo, useState } from 'react';
import { Settings, Bell, History, Cog } from 'lucide-react';
import { Badge } from '@radix-ui/themes';
import * as Tabs from '@radix-ui/react-tabs';

import { Button } from '@/components/ui/button';
import { AlertList } from '@/components/alerts/alert-list';
import { AlertRuleList } from '@/components/alerts/alert-rule-list';
import { AlertRuleForm } from '@/components/alerts/alert-rule-form';
import { NotificationsSettings } from '@/components/notifications/notification-settings';

import {
  useGetAlertsQuery,
  useGetAlertsHistoryQuery,
  useGetAlertRulesQuery,
  usePostAlertRulesMutation,
  GetAlertRulesApiResponse,
  usePostAlertsByAlertIdAcknowledgeMutation,
  usePostAlertsByAlertIdResolveMutation,
  usePutAlertRulesByRuleIdMutation,
  useDeleteAlertRulesByRuleIdMutation,
  PostAlertRulesApiArg,
  useGetNotificationsEventTypesQuery,
  ConfigApi,
} from '@/services';
import { AlertRuleFormValues } from '@/types/alerts';
import { Device, DeviceDetail } from '@/types/device';
import { toast } from '@/components/ui/toaster';

type AlertRule = GetAlertRulesApiResponse['data']['items'][0];

interface AlertsTabProps {
  device: Device | DeviceDetail;
}

export function AlertsTab({ device }: AlertsTabProps) {
  const [selectedTab, setSelectedTab] = useState('current');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [isRuleFormOpen, setIsRuleFormOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<AlertRule | null>(null);

  const { data: eventTypes } = useGetNotificationsEventTypesQuery({ category: 'alert' });

  const alertEventTypes = useMemo(() => {
    if (!eventTypes?.data) return [];
    return eventTypes.data;
  }, [eventTypes]);

  const {
    data: alertsData,
    isLoading: isLoadingAlerts,
    isFetching: isFetchingAlerts,
  } = useGetAlertsQuery(
    {
      deviceId: device.id,
      includeRule: 'true',
    },
    {
      refetchOnMountOrArgChange: true,
      // Refresh every minute
      pollingInterval: 60000,
    },
  );

  // Get alert history
  const {
    data: historyData,
    isLoading: isLoadingHistory,
    isFetching: isFetchingHistory,
  } = useGetAlertsHistoryQuery(
    {
      deviceId: device.id,
      includeRule: 'true',
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Get alert rules
  const {
    data: rulesData,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
    refetch: refetchRules,
  } = useGetAlertRulesQuery(
    {
      deviceId: device.id,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Alert actions
  const [acknowledgeAlert, { isLoading: isAcknowledging }] = usePostAlertsByAlertIdAcknowledgeMutation();
  const [resolveAlert, { isLoading: isResolving }] = usePostAlertsByAlertIdResolveMutation();
  const [createRule, { isLoading: isCreatingRule }] = usePostAlertRulesMutation();
  const [updateRule, { isLoading: isUpdatingRule }] = usePutAlertRulesByRuleIdMutation();
  const [deleteRule, { isLoading: isDeletingRule }] = useDeleteAlertRulesByRuleIdMutation();

  // Extract data from API responses
  const alerts = alertsData?.data?.items || [];
  const alertHistory = historyData?.data?.items || [];
  const alertRules = rulesData?.data?.items || [];

  // Get available sensor types for the rule form
  const sensorTypes: string[] = Array.from(
    new Set(device.sensors?.map((sensor: any) => sensor.type).filter(Boolean) || []),
  );

  // Calculate alert counts for the badge
  const activeAlertCount = alertsData?.data?.total || 0;

  // Action handlers
  const handleAcknowledgeAlert = (alertId: string) => {
    acknowledgeAlert({
      alertId,
      body: { note: 'Acknowledged from device dashboard' },
    });
  };

  const handleResolveAlert = (alertId: string) => {
    resolveAlert({
      alertId,
      body: { note: 'Resolved from device dashboard' },
    });
  };

  const handleCreateOrUpdateRule = async (formData: AlertRuleFormValues) => {
    const selectedEventType = alertEventTypes.find((e) => e.code == formData.event_type);

    if (!selectedEventType) {
      toast.error(`Event type not selected`);
      return;
    }

    const ruleData: PostAlertRulesApiArg['body'] = {
      ...formData,
      notification_event_type_id: selectedEventType.id,
      device_id: device.id,
    };

    if (selectedRule) {
      // Update existing rule
      await updateRule({
        ruleId: selectedRule.id,
        body: ruleData,
      })
        .then(() => {
          toast.success('Alert rule updated successfully');
        })
        .catch((error) => {
          toast.error(`Failed to update alert rule: ${error.message}`);
        });
    } else {
      // Create new rule
      await createRule({
        body: ruleData,
      })
        .then(() => {
          toast.success('Alert rule created successfully');
        })
        .catch((error) => {
          toast.error(`Failed to create alert rule: ${error.message}`);
        });
    }
    refetchRules();

    setIsRuleFormOpen(false);
    setSelectedRule(null);
  };

  const handleEditRule = (rule: AlertRule) => {
    setSelectedRule(rule);
    setIsRuleFormOpen(true);
  };

  const handleDeleteRule = (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this rule? This action cannot be undone.')) {
      deleteRule({
        ruleId,
      });
    }
  };

  const handleToggleRuleStatus = (rule: AlertRule) => {
    const newStatus = rule.status === 'active' ? 'paused' : 'active';
    updateRule({
      ruleId: rule.id,
      body: {
        device_id: device.id,
        status: newStatus,
      },
    });
  };

  return (
    <div className="space-y-6 pt-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            Device Alerts
            {activeAlertCount > 0 && (
              <Badge radius="full" color="red" size="2">
                {activeAlertCount}
              </Badge>
            )}
          </h2>
          <p className="text-muted-foreground mt-1">Monitor and manage device alerts and notifications</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setIsRuleFormOpen(true)}>
            <Cog className="w-4 h-4 mr-2" />
            New Rule
          </Button>
          <Button variant="outline" size="sm" onClick={() => setIsNotificationsOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Configure Notifications
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs.Root defaultValue="current" value={selectedTab} onValueChange={setSelectedTab}>
        <Tabs.List className="flex w-full border-b mb-6">
          <Tabs.Trigger
            value="current"
            className="flex-1 py-3 px-4 border-b-2 data-[state=active]:border-primary data-[state=active]:font-medium data-[state=inactive]:border-transparent data-[state=active]:text-primary"
          >
            <div className="flex items-center justify-center gap-2">
              <Bell className="h-4 w-4" />
              <span>Current Alerts</span>
              {activeAlertCount > 0 && (
                <Badge radius="full" color="red" variant="soft" size="1">
                  {activeAlertCount}
                </Badge>
              )}
            </div>
          </Tabs.Trigger>
          <Tabs.Trigger
            value="history"
            className="flex-1 py-3 px-4 border-b-2 data-[state=active]:border-primary data-[state=active]:font-medium data-[state=inactive]:border-transparent data-[state=active]:text-primary"
          >
            <div className="flex items-center justify-center gap-2">
              <History className="h-4 w-4" />
              <span>Alert History</span>
            </div>
          </Tabs.Trigger>
          <Tabs.Trigger
            value="rules"
            className="flex-1 py-3 px-4 border-b-2 data-[state=active]:border-primary data-[state=active]:font-medium data-[state=inactive]:border-transparent data-[state=active]:text-primary"
          >
            <div className="flex items-center justify-center gap-2">
              <Settings className="h-4 w-4" />
              <span>Alert Rules</span>
              <Badge radius="full" color="gray" variant="soft" size="1">
                {alertRules.length}
              </Badge>
            </div>
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="current" className="space-y-4">
          <AlertList
            alerts={alerts}
            isLoading={isLoadingAlerts || isFetchingAlerts}
            onAcknowledge={handleAcknowledgeAlert}
            onResolve={handleResolveAlert}
            selectedSeverity={filterSeverity}
            onChangeSeverity={setFilterSeverity}
            showResolveButton={true}
            isActionLoading={isAcknowledging || isResolving}
          />
        </Tabs.Content>

        <Tabs.Content value="history" className="space-y-4">
          <AlertList
            alerts={alertHistory}
            isLoading={isLoadingHistory || isFetchingHistory}
            onAcknowledge={handleAcknowledgeAlert}
            selectedSeverity={filterSeverity}
            onChangeSeverity={setFilterSeverity}
            isActionLoading={isAcknowledging}
          />
        </Tabs.Content>

        <Tabs.Content value="rules" className="space-y-4">
          <AlertRuleList
            rules={alertRules}
            isLoading={isLoadingRules}
            isFetching={isFetchingRules}
            onEdit={handleEditRule}
            onDelete={handleDeleteRule}
            onToggleStatus={handleToggleRuleStatus}
            onCreateNew={() => {
              setSelectedRule(null);
              setIsRuleFormOpen(true);
            }}
            isActionLoading={isDeletingRule}
          />
        </Tabs.Content>
      </Tabs.Root>

      {/* Alert Rule Form Modal */}
      <AlertRuleForm
        isOpen={isRuleFormOpen}
        onClose={() => {
          setIsRuleFormOpen(false);
          setSelectedRule(null);
        }}
        onSubmit={handleCreateOrUpdateRule}
        rule={selectedRule}
        devices={[{ id: device.id, name: device.name || device.serial_number }]}
        sensorTypes={sensorTypes}
        eventTypes={alertEventTypes}
        isLoading={isCreatingRule || isUpdatingRule}
      />

      {/* Notification Settings Modal */}
      <NotificationsSettings
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        settingType="device"
      />
    </div>
  );
}
